import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wrench, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_BG = {
  Repaired: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Attribution removed": "bg-amber-50 text-amber-700 border-amber-200",
  "Marked not independently verified": "bg-red-50 text-red-700 border-red-200",
  Recalculated: "bg-teal-50 text-teal-700 border-teal-200",
  Reconciled: "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Reconciled — no change": "bg-slate-50 text-slate-500 border-slate-200",
  "No change": "bg-slate-50 text-slate-500 border-slate-200",
};

function ConfirmRepairDialog({ onClose, onConfirm, busy }) {
  const [returnTo] = useState(() => document.activeElement);
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="repair-confirm-dialog" aria-busy={busy}
        onCloseAutoFocus={(e) => { e.preventDefault(); returnTo?.focus?.(); }}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Run complete project repair</DialogTitle>
          <DialogDescription className="text-xs text-slate-600 leading-snug">
            This reconciles all declared totals, removes named attributions that cannot be independently verified
            (statements are retained anonymised), corrects the EMERALD subgroup citation, marks non-verifiable records
            "Not independently verified", and recalculates every HCP count, consensus value and confidence value.
            A before/after discrepancy report is filed. The action is logged and cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="h-8 text-xs" disabled={busy} onClick={onClose} data-testid="repair-cancel-btn">Cancel</Button>
          <Button className="h-8 text-xs bg-slate-900 text-white hover:bg-slate-800" disabled={busy} onClick={onConfirm} data-testid="repair-confirm-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" aria-hidden="true" />}{busy ? "Repairing project…" : "Run repair"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const ProjectRepair = () => {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openArea, setOpenArea] = useState(null);

  const load = () => {
    setLoadError(false);
    api.get("/project/repair").then((r) => setStatus(r.data)).catch(() => setLoadError(true));
  };
  useEffect(load, []);

  const run = () => {
    setBusy(true);
    api.post("/project/repair/run")
      .then((r) => {
        load();
        setConfirming(false);
        toast.success(`${r.data.run_id} complete — ${r.data.summary.discrepancies_resolved} discrepancies resolved.`);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Repair run failed — please retry.",
        { action: { label: "Retry", onClick: run } }))
      .finally(() => setBusy(false));
  };

  const rep = status?.latest;
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm shadow-slate-100" data-testid="project-repair-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Wrench className="w-4 h-4 text-teal-600 mt-0.5" aria-hidden="true" />
          <div>
            <h3 className="font-heading text-sm font-bold text-slate-900">Complete project repair &amp; discrepancy report</h3>
            <p className="text-xs text-slate-500 mt-0.5 max-w-3xl">{status?.policy || "Reconciliation, attribution verification, citation correction and full recalculation with a before/after discrepancy report."}</p>
          </div>
        </div>
        <Button className="h-8 text-xs bg-slate-900 text-white hover:bg-slate-800" onClick={() => setConfirming(true)}
          data-testid="repair-run-btn" aria-label="Run complete project repair">
          <Wrench className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />{rep ? "Re-run repair" : "Run complete repair"}
        </Button>
      </div>

      {!status && !loadError && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-3" role="status" aria-live="polite" data-testid="repair-loading">
          <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />Loading repair status…
        </p>
      )}
      {loadError && (
        <p className="text-[11px] text-red-600 flex items-center gap-2 mt-3" role="alert" data-testid="repair-load-error">
          Repair status could not be loaded.
          <Button variant="outline" className="h-6 text-[10px]" onClick={load} data-testid="repair-retry-btn">Retry</Button>
        </p>
      )}
      {status && !rep && (
        <p className="text-xs text-slate-500 mt-3" data-testid="repair-never-run">
          No repair run on record. The demonstration project still carries illustrative declared volumes, unverified named
          attributions and one open citation conflict — run the repair to reconcile and file the discrepancy report.
        </p>
      )}

      {rep && (
        <div className="mt-4" data-testid="repair-report">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-mono text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{rep.run_id}</span>
            <span className="text-[11px] text-slate-500">Run by {rep.run_by} · {new Date(rep.run_at).toLocaleString()}{rep.repeat_run ? " · repeat run" : ""}</span>
            {status.last_run_at && status.last_run_at !== rep.run_at && (
              <span className="text-[10px] text-slate-400" data-testid="repair-last-verified">
                Re-verified {new Date(status.last_run_at).toLocaleString()} — no further discrepancies
              </span>
            )}
            {[["Discrepancies resolved", rep.summary.discrepancies_resolved, "text-emerald-700"],
              ["Attributions removed", rep.summary.attributions_removed, "text-amber-700"],
              ["Marked not independently verified", rep.summary.marked_not_independently_verified, "text-red-700"],
              ["Statements removed", rep.summary.statements_removed, "text-slate-600"],
              ["Unchanged line items", rep.summary.no_change, "text-slate-500"]].map(([l, v, c]) => (
              <span key={l} className={`text-[10.5px] font-semibold ${c} bg-slate-50 border border-slate-200 rounded-full px-2.5 py-0.5`} data-testid={`repair-chip-${l.toLowerCase().replace(/ /g, "-")}`}>{v} {l.toLowerCase()}</span>
            ))}
          </div>
          {rep.sections.map((sec, si) => (
            <div key={sec.area} className="border border-slate-200 rounded-lg mb-2 overflow-hidden" data-testid={`repair-section-${si}`}>
              <button onClick={() => setOpenArea(openArea === si ? null : si)} aria-expanded={openArea === si}
                aria-label={`${openArea === si ? "Collapse" : "Expand"} ${sec.area} discrepancies`}
                className="w-full flex items-center justify-between px-3.5 py-2.5 bg-slate-50 hover:bg-slate-100 text-left"
                data-testid={`repair-section-toggle-${si}`}>
                <span className="text-xs font-bold text-slate-800">{sec.area}</span>
                <span className="text-[10px] font-mono text-slate-400">
                  {sec.items.filter((i) => i.changed).length}/{sec.items.length} changed
                </span>
              </button>
              {openArea === si && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" aria-label={`${sec.area} — before and after discrepancies`}>
                    <thead>
                      <tr className="text-left text-[9px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-200">
                        <th scope="col" className="py-2 px-3">Item</th><th scope="col" className="py-2 pr-3">Before</th>
                        <th scope="col" className="py-2 pr-3" aria-hidden="true"></th>
                        <th scope="col" className="py-2 pr-3">After</th><th scope="col" className="py-2 pr-3">Action taken</th>
                        <th scope="col" className="py-2 pr-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sec.items.map((it, ii) => (
                        <tr key={ii} className="border-b border-slate-100 align-top" data-testid={`repair-item-${si}-${ii}`}>
                          <td className="py-2 px-3 font-semibold text-slate-800 max-w-[220px] leading-snug">{it.item}</td>
                          <td className={`py-2 pr-3 max-w-[200px] leading-snug ${it.changed ? "text-slate-400 line-through decoration-slate-300" : "text-slate-500"}`}>{it.before}</td>
                          <td className="py-2 pr-2"><ArrowRight className="w-3 h-3 text-slate-300" aria-hidden="true" /></td>
                          <td className="py-2 pr-3 font-semibold text-teal-800 max-w-[220px] leading-snug">{it.after}</td>
                          <td className="py-2 pr-3 text-slate-500 max-w-xs leading-snug">{it.action}</td>
                          <td className="py-2 pr-3"><Badge variant="outline" className={`text-[9.5px] whitespace-nowrap ${STATUS_BG[it.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{it.status}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {confirming && <ConfirmRepairDialog onClose={() => setConfirming(false)} onConfirm={run} busy={busy} />}
    </div>
  );
};
